import { Button } from '@material-ui/core';
import { last } from 'lodash-es';
import moment from 'moment-timezone';
import React, { ReactElement } from 'react';

import { IAdserverGlobalVast } from '~/src/dash/api/adserver-global-vast';
import {
  IAdserverCreativeFlightDatesSubgroup,
  IAdserverGlobalMediafile,
} from '~/src/dash/api/console-api';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateFromUTCToOffset, formatDateWithOffsetToUTC } from '~/src/dash/utils/date';
import { IPermissionCheck } from '~/src/dash/utils/permission_service';
import { getEmptyCreativeCheckpoint } from '~/src/dash/views/campaign/common';
import { CreativeSubgroup } from '~/src/dash/views/campaign/LineItem/CreativeSubGroups/CreativeSubgroup';

interface ICreativeSubGroups {
  adserverCampaignID: string;
  blankMediaFileList: IMenuList;
  blankVastList: IMenuList;
  campaignID: string;
  classes: { [key: string]: string };
  creativeType: string;
  defaultEndDate: moment.Moment | undefined;
  defaultStartDate: moment.Moment | undefined;
  forceMenuButtonClosed: boolean;
  globalMediaFiles: IAdserverGlobalMediafile[];
  handleChangeVariantActive: (variantIdx: number, gmfIdx: number) => void;
  handleOpenCreateCheckpointModal: (creativeID: string) => void;
  handleVastEditEnabled: (index: number, vast?: IAdserverGlobalVast) => void;
  isLoadingMediaFilesAndVasts: boolean;
  isNativeVastAuthorized: boolean;
  onSubgroupsChange: (subgroups: IAdserverCreativeFlightDatesSubgroup[]) => void;
  mediaFileOptions: ISelectOption[];
  removeGlobalMediafile: (creativeIdx: number, mediafileID: string) => void;
  renderCreateBannerFromAssetURLRow: () => ReactElement;
  renderCreateFromAssetURLRow: (
    cidx: number,
    GlobalMediaFiles: IAdserverGlobalMediafile[],
    subgroupIdx?: number, // if EnableFlightDateSubgroups is true, then we need the subgroup index for AdserverCreativeFlightDatesSubgroups.
  ) => ReactElement;
  renderMenuTitle: (inMenu: boolean, disabled: boolean) => ReactElement;
  renderUploadBannerRow: (
    cidx: number,
    GlobalMediaFiles: IAdserverGlobalMediafile[],
    subgroupIdx?: number, // if EnableFlightDateSubgroups is true, then we need the subgroup index for AdserverCreativeFlightDatesSubgroups.
  ) => ReactElement;
  renderUploadRow: (
    cidx: number,
    GlobalMediaFiles: IAdserverGlobalMediafile[],
    subgroupIdx?: number, // if EnableFlightDateSubgroups is true, then we need the subgroup index for AdserverCreativeFlightDatesSubgroups.
  ) => ReactElement;
  setState: (state: Record<string, any>) => void;
  subgroups: IAdserverCreativeFlightDatesSubgroup[];
  tempVastURL: string;
  timezone: string;
  user: IUserState;
  vastEditIndex: number;
  vastOptions: ISelectOption[];
  vasts: IAdserverGlobalVast[];
  writeAuthorization: IPermissionCheck;
}
export const CreativeSubGroups = ({
  adserverCampaignID,
  blankMediaFileList,
  blankVastList,
  campaignID,
  classes,
  creativeType,
  defaultEndDate,
  defaultStartDate,
  forceMenuButtonClosed,
  globalMediaFiles,
  handleChangeVariantActive,
  handleOpenCreateCheckpointModal,
  handleVastEditEnabled,
  isLoadingMediaFilesAndVasts,
  isNativeVastAuthorized,
  onSubgroupsChange,
  mediaFileOptions,
  removeGlobalMediafile,
  renderCreateBannerFromAssetURLRow,
  renderCreateFromAssetURLRow,
  renderMenuTitle,
  renderUploadBannerRow,
  renderUploadRow,
  setState,
  subgroups,
  tempVastURL,
  timezone,
  user,
  vastEditIndex,
  vastOptions,
  vasts,
  writeAuthorization,
}: ICreativeSubGroups) => {
  const onSubgroupChange = (
    newSubgroup: IAdserverCreativeFlightDatesSubgroup,
    subgroupIndex: number,
  ) => {
    subgroups[subgroupIndex] = newSubgroup;
    onSubgroupsChange(subgroups);
  };

  const addFlightDatesCreativeSubgroup = () => {
    let EndDate = defaultEndDate ? formatDateWithOffsetToUTC(defaultEndDate, timezone) : 'null';
    let StartDate = defaultStartDate
      ? formatDateWithOffsetToUTC(defaultStartDate, timezone)
      : 'null';

    const lastSubgroupEndDate = last(subgroups)?.EndDate;
    if (lastSubgroupEndDate && lastSubgroupEndDate !== 'null') {
      const endDate = formatDateFromUTCToOffset(lastSubgroupEndDate, timezone);
      StartDate = formatDateWithOffsetToUTC(endDate.clone().add(1, 'minute'), timezone);
      EndDate = formatDateWithOffsetToUTC(endDate.clone().add(1, 'day'), timezone);
    }

    onSubgroupsChange([...subgroups, getEmptyCreativeCheckpoint(StartDate, EndDate)]);
  };

  return (
    <>
      {subgroups.map((subgroup: IAdserverCreativeFlightDatesSubgroup, subgroupIdx: number) => (
        <CreativeSubgroup
          key={subgroup.ID === '0' ? subgroupIdx : subgroup.ID} // new checkpoint does not have unique id to be used as key
          adserverCampaignID={adserverCampaignID}
          blankMediaFileList={blankMediaFileList}
          blankVastList={blankVastList}
          campaignID={campaignID}
          classes={classes}
          creativeType={creativeType}
          defaultStartDate={
            defaultStartDate ? formatDateWithOffsetToUTC(defaultStartDate, timezone) : 'null'
          }
          defaultEndDate={
            defaultEndDate ? formatDateWithOffsetToUTC(defaultEndDate, timezone) : 'null'
          }
          forceMenuButtonClosed={forceMenuButtonClosed}
          globalMediaFiles={globalMediaFiles}
          handleChangeVariantActive={handleChangeVariantActive}
          handleOpenCreateCheckpointModal={handleOpenCreateCheckpointModal}
          handleVastEditEnabled={handleVastEditEnabled}
          isLoadingMediaFilesAndVasts={isLoadingMediaFilesAndVasts}
          isNativeVastAuthorized={isNativeVastAuthorized}
          onSubgroupChange={onSubgroupChange}
          mediaFileOptions={mediaFileOptions}
          removeGlobalMediafile={removeGlobalMediafile}
          removeSubgroup={() => {
            const newSubgroups = [...subgroups];
            newSubgroups.splice(subgroupIdx, 1);
            onSubgroupsChange(newSubgroups);
          }}
          renderCreateBannerFromAssetURLRow={renderCreateBannerFromAssetURLRow}
          renderCreateFromAssetURLRow={renderCreateFromAssetURLRow}
          renderMenuTitle={renderMenuTitle}
          renderUploadBannerRow={renderUploadBannerRow}
          renderUploadRow={renderUploadRow}
          setState={setState}
          subgroup={subgroup}
          subgroupIdx={subgroupIdx}
          tempVastURL={tempVastURL}
          timezone={timezone}
          user={user}
          vastEditIndex={vastEditIndex}
          vastOptions={vastOptions}
          vasts={vasts}
          writeAuthorization={writeAuthorization}
        />
      ))}
      <div className={classes.section}>
        <Button
          style={{
            color: writeAuthorization.isAuthorized ? publicaColors.blue : publicaColors.lightGray,
          }}
          classes={{ root: classes.button }}
          onClick={addFlightDatesCreativeSubgroup}
          disabled={!writeAuthorization.isAuthorized}
        >
          + Add Creative Checkpoint
        </Button>
      </div>
    </>
  );
};
